/**
 * Auth feature — Ducks pattern (actions, reducer, types).
 *
 * Owns the Redux slice for authenticated user identity. Under BFF auth
 * the chores backend holds the OAuth tokens; the SPA only knows
 * whether the cookie identifies a user. We model that as
 * isAuthenticated (true once GET /api/v1/auth/me returns a SafeUser).
 * The legacy three-way sessionStatus state machine ('loading' |
 * 'anonymous' | 'authenticated') and its hasBeenAuthenticated sticky
 * flag are gone — both were workarounds for the foauth/web silent-
 * refresh flicker that doesn't exist under BFF (server-side refresh).
 */

// ─── State Shape ───────────────────────────────────────────

// Mirrors the proto SafeUser shape (display_name, optional role).
export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  role?: string;
  timezone?: string;
  // Hour [0,8] that begins the user's logical day. Defaults to 6 at
  // the consumer side when missing.
  day_start_hour?: number;
  // Hour [0,23] for new-todo default due time. Defaults to 10 at the
  // consumer side when missing.
  default_hour?: number;
}

export interface AuthState {
  user: AuthUser | null;
  // True while the boot probe (GET /api/v1/auth/me) is in flight. The
  // initial render gates on this to avoid flashing /login for a
  // logged-in user.
  isLoading: boolean;
  error: string | null;
  familyView: boolean;
}

// Family View preference is a pure-UI toggle. We persist it to localStorage
// directly from the reducer — there's no server round-trip to coordinate and
// no reason to thread it through a saga.
const FAMILY_VIEW_STORAGE_KEY = 'familyView';

function readFamilyViewPref(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FAMILY_VIEW_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeFamilyViewPref(next: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FAMILY_VIEW_STORAGE_KEY, next ? '1' : '0');
  } catch {
    // Private-mode / quota — fall through; in-memory state still reflects the toggle.
  }
}

const initialState: AuthState = {
  user: null,
  isLoading: true, // boot probe runs before first render
  error: null,
  familyView: readFamilyViewPref(),
};

// ─── Action Types ──────────────────────────────────────────

export const LOGIN_REQUESTED = 'auth/loginRequested';
const LOGIN_SUCCESS = 'auth/loginSuccess';
const LOGIN_FAILURE = 'auth/loginFailure';
export const LOGOUT_REQUESTED = 'auth/logoutRequested';
const LOGOUT = 'auth/logout';
const SET_ERROR = 'auth/setError';
const CLEAR_ERROR = 'auth/clearError';
export const SESSION_PROBE_COMPLETE = 'auth/sessionProbeComplete';
const TOGGLE_FAMILY_VIEW = 'auth/toggleFamilyView';

// ─── Action Creators ───────────────────────────────────────

export function loginRequested(payload: { returnTo?: string }) {
  return { type: LOGIN_REQUESTED, payload } as const;
}

export function loginSuccess(payload: { user: AuthUser }) {
  return { type: LOGIN_SUCCESS, payload } as const;
}

export function loginFailure(payload: { error: string }) {
  return { type: LOGIN_FAILURE, payload } as const;
}

export function logoutRequested() {
  return { type: LOGOUT_REQUESTED } as const;
}

export function logout() {
  return { type: LOGOUT } as const;
}

export function setError(payload: { error: string }) {
  return { type: SET_ERROR, payload } as const;
}

export function clearError() {
  return { type: CLEAR_ERROR } as const;
}

// sessionProbeComplete fires once after the boot-time GET /auth/me
// resolves, regardless of outcome. The reducer flips isLoading=false
// so ProtectedRoute can render its final decision instead of staying
// on <Loading/> forever when the user is anonymous.
export function sessionProbeComplete() {
  return { type: SESSION_PROBE_COMPLETE } as const;
}

export function toggleFamilyView() {
  return { type: TOGGLE_FAMILY_VIEW } as const;
}

// ─── Action Type Union ─────────────────────────────────────

type AuthAction =
  | ReturnType<typeof loginRequested>
  | ReturnType<typeof loginSuccess>
  | ReturnType<typeof loginFailure>
  | ReturnType<typeof logoutRequested>
  | ReturnType<typeof logout>
  | ReturnType<typeof setError>
  | ReturnType<typeof clearError>
  | ReturnType<typeof sessionProbeComplete>
  | ReturnType<typeof toggleFamilyView>;

// ─── Reducer ───────────────────────────────────────────────

export function authReducer(
  state: AuthState = initialState,
  action: AuthAction | { type: string; payload?: unknown }
): AuthState {
  switch (action.type) {
    case LOGIN_REQUESTED: {
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    }

    case LOGIN_SUCCESS: {
      const { user } = (action as ReturnType<typeof loginSuccess>).payload;
      return {
        ...state,
        user,
        isLoading: false,
        error: null,
      };
    }

    case LOGIN_FAILURE: {
      const { error } = (action as ReturnType<typeof loginFailure>).payload;
      return {
        ...state,
        isLoading: false,
        error,
        user: null,
      };
    }

    case LOGOUT: {
      return {
        ...state,
        user: null,
        error: null,
        isLoading: false,
      };
    }

    case SET_ERROR: {
      const { error } = (action as ReturnType<typeof setError>).payload;
      return {
        ...state,
        error,
      };
    }

    case CLEAR_ERROR: {
      return {
        ...state,
        error: null,
      };
    }

    case SESSION_PROBE_COMPLETE: {
      return {
        ...state,
        isLoading: false,
      };
    }

    case TOGGLE_FAMILY_VIEW: {
      const next = !state.familyView;
      writeFamilyViewPref(next);
      return {
        ...state,
        familyView: next,
      };
    }

    default:
      return state;
  }
}

export default authReducer;
